import {
    getInvoiceDetailsByIdAPI,
    chargeInvoiceAPI,
    DownloadInvoiceAPI,
    processPaymentAPI,
    sendInvoiceSummaryEmailAPI,
} from "../../../utils/api.js";
import { errorAlert, successAlert } from "../../../utils/helper.js";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getInvoiceDetailsById = createAsyncThunk(
    "invoice/invoice-details",
    async (invoiceId, { rejectWithValue }) => {
      try {
        const response = await getInvoiceDetailsByIdAPI(invoiceId);
        return response;
      } catch (error) {
        return rejectWithValue(error);
      }
    }
);

export const chargeInvoice = createAsyncThunk(
  "invoice/charge-customer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await chargeInvoiceAPI(data);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const downloadInvoiceDetailsById = createAsyncThunk(
  "invoice/invoice-summary",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await DownloadInvoiceAPI(invoiceId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const processPayment = createAsyncThunk(
  "payment/processPayment",
  async ({ tripId, amount, paymentMethodId }, { rejectWithValue }) => {
    try {
      const response = await processPaymentAPI(tripId, amount, paymentMethodId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const sendInvoiceSummaryEmail = createAsyncThunk(
  "invoice/sendInvoiceSummaryEmail",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await sendInvoiceSummaryEmailAPI(invoiceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

const initialState = {
    invoice: [],
    url: [],
    status: 'idle',
    error: null,
    saving: false,
    loading: false,
    emailSending: false,
};

const invoiceSlice = createSlice({
    name: 'invoice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder

      .addCase(getInvoiceDetailsById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoiceDetailsById.fulfilled, (state, action) => {
        state.invoice = action?.payload?.data;
        state.error = null;
        state.loading = false;
      })
      .addCase(getInvoiceDetailsById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })

      .addCase(chargeInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(chargeInvoice.fulfilled, (state) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(chargeInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })

      .addCase(downloadInvoiceDetailsById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadInvoiceDetailsById.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        const payloadData = action?.payload?.data || action?.payload;

        if (payloadData instanceof Blob || (payloadData && payloadData.type === 'application/pdf')) {
            const blob = new Blob([payloadData], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `invoice_summary.pdf`);
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            }, 500);
        } else if (action?.payload?.data?.pdfUrl) {
            const downloadUrl = action.payload.data.pdfUrl;
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            setTimeout(() => document.body.removeChild(link), 500);
        } else {
          errorAlert("Could Not Download Invoice Summary");
        }
      })
      .addCase(downloadInvoiceDetailsById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
        errorAlert("Could Not Download Invoice Summary");
      })

      .addCase(processPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processPayment.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        successAlert("Payment processed successfully.");
      })
      .addCase(processPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        errorAlert(state.error || "Error! Unable to process payment.");
      })

      // ─── Send Invoice Summary Email ──────────────
      .addCase(sendInvoiceSummaryEmail.pending, (state) => {
        state.emailSending = true;
      })
      .addCase(sendInvoiceSummaryEmail.fulfilled, (state) => {
        state.emailSending = false;
        successAlert("Invoice summary email sent successfully!");
      })
      .addCase(sendInvoiceSummaryEmail.rejected, (state, action) => {
        state.emailSending = false;
        errorAlert(action.payload?.message || "Failed to send invoice summary email");
      });
    },
});

export const selectInvoice = (state) => state.invoice.invoice;
export const selectInvoiceEmailSending = (state) => state.invoice.emailSending;
export default invoiceSlice.reducer;
